import * as t from 'io-ts';

import {makeEnumRuntimeType} from './apiTypes';
import {CalendarWeekDaysEnum, RCalendarWeekDay} from './calendarCommonTypes';

/*
 * Runtime type.
 */

export enum RecurrenceFrequenciesEnum {
  YEARLY = 'yearly',
  MONTHLY = 'monthly',
  WEEKLY = 'weekly',
  DAILY = 'daily',
  HOURLY = 'hourly',
  MINUTELY = 'minutely',
  SECONDLY = 'secondly',
}

const RWeekdayDirty = t.readonly(
  t.type({
    weekday: RCalendarWeekDay,
    n: t.union([t.Integer, t.undefined]),
  }),
);

const RRecurrenceRuleDirty = t.readonly(
  t.type({
    freq: makeEnumRuntimeType(RecurrenceFrequenciesEnum),
    dtstart: t.union([t.Integer, t.undefined]),
    interval: t.union([t.Integer, t.undefined]),
    wkst: t.union([RCalendarWeekDay, t.undefined]),
    count: t.union([t.Integer, t.undefined]),
    until: t.union([t.Integer, t.undefined]),
    tzid: t.union([t.string, t.undefined]),
    bysetpos: t.union([t.readonlyArray(t.Integer), t.undefined]),
    bymonth: t.union([t.readonlyArray(t.Integer), t.undefined]),
    bymonthday: t.union([t.readonlyArray(t.Integer), t.undefined]),
    byyearday: t.union([t.readonlyArray(t.Integer), t.undefined]),
    byweekno: t.union([t.readonlyArray(t.Integer), t.undefined]),
    byweekday: t.union([t.readonlyArray(RWeekdayDirty), t.undefined]),
    byhour: t.union([t.readonlyArray(t.Integer), t.undefined]),
    byminute: t.union([t.readonlyArray(t.Integer), t.undefined]),
    bysecond: t.union([t.readonlyArray(t.Integer), t.undefined]),
  }),
);

const RRecurrenceDateDirty = t.readonly(
  t.type({
    rdates: t.readonlyArray(t.Integer),
  }),
);

const RExceptionDateDirty = t.readonly(
  t.type({
    exdates: t.readonlyArray(t.Integer),
  }),
);

export const RRecurrenceDirty = t.readonly(
  t.intersection([
    t.type({
      rrules: t.readonlyArray(RRecurrenceRuleDirty),
    }),
    RRecurrenceDateDirty,
    RExceptionDateDirty,
  ]),
);

/*
 * Type.
 */

export interface RecurrenceWeekdayModel extends t.TypeOf<typeof RWeekdayDirty> {}
export interface RecurrenceRuleModel extends t.TypeOf<typeof RRecurrenceRuleDirty> {}
export interface RecurrenceDateModel extends t.TypeOf<typeof RRecurrenceDateDirty> {}
export interface ExceptionDateModel extends t.TypeOf<typeof RExceptionDateDirty> {}
export interface RecurrenceModel extends t.TypeOf<typeof RRecurrenceDirty> {}

/*
 * IO methods.
 */

export function parseRecurrence(src: any): RecurrenceModel {
  const rrules: RecurrenceRuleModel[] = [];
  const rdates: number[] = [];
  const exdates: number[] = [];

  if (!src || src.length === 0) {
    return {rrules, rdates, exdates};
  }

  src.forEach((recurrence: any) => {
    if (recurrence.freq) {
      rrules.push(parseRecurrenceRule(recurrence));
    } else if (recurrence.rdates) {
      rdates.push(...recurrence.rdates);
    } else if (recurrence.exdates) {
      exdates.push(...recurrence.exdates);
    }
  });

  return {
    rrules,
    exdates,
    rdates,
  };
}

enum RecurrenceWeekdaysEnum {
  SUNDAY = 'Sunday',
  MONDAY = 'Monday',
  TUESDAY = 'Tuesday',
  WEDNESDAY = 'Wednesday',
  THURSDAY = 'Thursday',
  FRIDAY = 'Friday',
  SATURDAY = 'Saturday',
}

const RecurrenceWeekdayToCalendarWeekday: {[_: string]: CalendarWeekDaysEnum} = {
  [RecurrenceWeekdaysEnum.SUNDAY]: CalendarWeekDaysEnum.SUNDAY,
  [RecurrenceWeekdaysEnum.MONDAY]: CalendarWeekDaysEnum.MONDAY,
  [RecurrenceWeekdaysEnum.TUESDAY]: CalendarWeekDaysEnum.TUESDAY,
  [RecurrenceWeekdaysEnum.WEDNESDAY]: CalendarWeekDaysEnum.WEDNESDAY,
  [RecurrenceWeekdaysEnum.THURSDAY]: CalendarWeekDaysEnum.THURSDAY,
  [RecurrenceWeekdaysEnum.FRIDAY]: CalendarWeekDaysEnum.FRIDAY,
  [RecurrenceWeekdaysEnum.SATURDAY]: CalendarWeekDaysEnum.SATURDAY,
} as const;

const CalendarWeekdayToRecurrenceWeekday: {[_: string]: RecurrenceWeekdaysEnum} = {
  [CalendarWeekDaysEnum.SUNDAY]: RecurrenceWeekdaysEnum.SUNDAY,
  [CalendarWeekDaysEnum.MONDAY]: RecurrenceWeekdaysEnum.MONDAY,
  [CalendarWeekDaysEnum.TUESDAY]: RecurrenceWeekdaysEnum.TUESDAY,
  [CalendarWeekDaysEnum.WEDNESDAY]: RecurrenceWeekdaysEnum.WEDNESDAY,
  [CalendarWeekDaysEnum.THURSDAY]: RecurrenceWeekdaysEnum.THURSDAY,
  [CalendarWeekDaysEnum.FRIDAY]: RecurrenceWeekdaysEnum.FRIDAY,
  [CalendarWeekDaysEnum.SATURDAY]: RecurrenceWeekdaysEnum.SATURDAY,
};

function parseRecurrenceRule(src: any): RecurrenceRuleModel {
  return {
    freq: src.freq,
    dtstart: src.dtstart || undefined,
    interval: src.interval || undefined,
    wkst: RecurrenceWeekdayToCalendarWeekday[src.wkst] || undefined,
    count: src.count || undefined,
    until: src.until || undefined,
    tzid: src.tzid || undefined,
    bysetpos: src.bysetpos || undefined,
    bymonth: src.bymonth || undefined,
    bymonthday: src.bymonthday || undefined,
    byyearday: src.byyearday || undefined,
    byweekno: src.byweekno || undefined,
    byweekday: src.byweekday ? src.byweekday.map(parseWeekday) : undefined,
    byhour: src.byhour || undefined,
    byminute: src.byminute || undefined,
    bysecond: src.bysecond || undefined,
  };
}

function parseWeekday(src: any) {
  return {
    weekday: RecurrenceWeekdayToCalendarWeekday[src.weekday],
    n: src.n || undefined,
  };
}

export function serializeRecurrence(recurrence: RecurrenceModel) {
  const serializedRecurrence: any = recurrence.rrules.map(serializeRecurrenceRule);

  if (recurrence.rdates.length > 0) {
    serializedRecurrence.push({rdates: recurrence.rdates});
  }
  if (recurrence.exdates.length > 0) {
    serializedRecurrence.push({exdates: recurrence.exdates});
  }

  return serializedRecurrence;
}

function serializeRecurrenceRule(rrule: RecurrenceRuleModel) {
  return {
    freq: rrule.freq,
    dtstart: rrule.dtstart || undefined,
    interval: rrule.interval || undefined,
    wkst: rrule.wkst ? CalendarWeekdayToRecurrenceWeekday[rrule.wkst] : undefined,
    count: rrule.count || undefined,
    until: rrule.until || undefined,
    tzid: rrule.tzid || undefined,
    bysetpos: rrule.bysetpos || undefined,
    bymonth: rrule.bymonth || undefined,
    bymonthday: rrule.bymonthday || undefined,
    byyearday: rrule.byyearday || undefined,
    byweekno: rrule.byweekno || undefined,
    byweekday: rrule.byweekday ? rrule.byweekday.map(serializeWeekday) : undefined,
    byhour: rrule.byhour || undefined,
    byminute: rrule.byminute || undefined,
    bysecond: rrule.bysecond || undefined,
  };
}

function serializeWeekday(recurrenceWeekday: RecurrenceWeekdayModel) {
  return {
    weekday: CalendarWeekdayToRecurrenceWeekday[recurrenceWeekday.weekday],
    n: recurrenceWeekday.n,
  };
}
